import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { User, Car, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { IMAGES } from "../mock";

export default function AppAuth() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [roles, setRoles] = useState(["passenger"]);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const toggleRole = (r) => {
    setRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login({ email: f.email, password: f.password });
        toast.success("Welcome back!");
      } else {
        if (!f.name.trim()) { toast.error("Please enter your name."); setLoading(false); return; }
        if (roles.length === 0) { toast.error("Choose at least one role."); setLoading(false); return; }
        await register({ name: f.name, email: f.email, password: f.password, roles });
        toast.success("Account created!");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090a] flex items-center justify-center px-4 id-radial">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={IMAGES.logo} alt="InstantDrive" className="h-12" />
        </div>
        <div className="rounded-3xl border border-white/10 bg-[#0e1311] p-8">
          <div className="flex rounded-xl bg-[#0a0e0c] border border-white/10 p-1 mb-6">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`auth-tab-${m}`} onClick={() => setMode(m)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold capitalize transition-all ${mode === m ? "bg-[#2fd645] text-[#06210b]" : "text-white/60"}`}>
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>
          <form data-testid="auth-form" onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-2">
                <Label className="text-white/80">Full name</Label>
                <Input className="h-12 bg-[#0a0e0c] border-white/15 text-white focus-visible:ring-[#2fd645]" data-testid="auth-name-input" placeholder="Your name" value={f.name} onChange={set("name")} />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-white/80">Email</Label>
              <Input type="email" className="h-12 bg-[#0a0e0c] border-white/15 text-white focus-visible:ring-[#2fd645]" data-testid="auth-email-input" placeholder="you@example.com" value={f.email} onChange={set("email")} />
            </div>
            <div className="space-y-2">
              <Label className="text-white/80">Password</Label>
              <Input type="password" className="h-12 bg-[#0a0e0c] border-white/15 text-white focus-visible:ring-[#2fd645]" data-testid="auth-password-input" placeholder="••••••" value={f.password} onChange={set("password")} />
            </div>
            {mode === "register" && (
              <div className="space-y-2">
                <Label className="text-white/80">I want to use InstantDrive as</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[{ id: "passenger", label: "Passenger", Icon: User }, { id: "driver", label: "Driver", Icon: Car }].map(({ id, label, Icon }) => (
                    <button type="button" key={id} data-testid={`auth-role-${id}`} onClick={() => toggleRole(id)}
                      className={`flex items-center justify-center gap-2 h-12 rounded-xl border font-semibold transition-all ${roles.includes(id) ? "border-[#2fd645] bg-[#2fd645]/10 text-[#2fd645]" : "border-white/15 text-white/70"}`}>
                      <Icon className="h-4 w-4" /> {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Button type="submit" data-testid="auth-submit-button" disabled={loading} className="w-full h-12 bg-[#2fd645] text-[#06210b] font-bold hover:bg-[#46ff5c] rounded-xl disabled:opacity-70">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "login" ? "Sign in" : "Create account")}
            </Button>
          </form>
        </div>
        <p className="text-center text-white/40 text-sm mt-6">
          <a href="/" className="hover:text-[#2fd645]">← Back to website</a>
        </p>
      </div>
    </div>
  );
}
